<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import ImockupTimeline from '@/components/imockup/ImockupTimeline.vue'
import { useMockupEditorStore } from '@/stores/mockupEditor'

const store = useMockupEditorStore()
const {
  formattedTime,
  totalDurationSeconds,
  timelineGranularity,
  timelineSnapStepSec,
  isPlaying,
  currentTimeSeconds,
  animationLayers,
  selectedLayerId,
  canUndoEditor,
  canRedoEditor,
} = storeToRefs(store)

const activeLayerLocked = computed(() => {
  const activeId = selectedLayerId.value
  if (!activeId) return false
  return animationLayers.value.find((layer) => layer.id === activeId)?.locked === true
})

const bodyScrollRef = ref<HTMLElement | null>(null)

function forwardVerticalWheelToSharedScroll(ev: WheelEvent): void {
  const host = bodyScrollRef.value
  if (!host) return
  if (Math.abs(ev.deltaY) <= Math.abs(ev.deltaX)) return
  const before = host.scrollTop
  host.scrollTop += ev.deltaY
  if (host.scrollTop !== before) ev.preventDefault()
}

/**
 * 当前播放头是否位于选中图层可见区间内。
 */
const playheadInsideActiveLayerVisibility = computed(() => {
  const activeId = selectedLayerId.value
  if (!activeId) return false
  const layer = animationLayers.value.find((item) => item.id === activeId)
  if (!layer) return false
  const t = currentTimeSeconds.value
  return t >= layer.visibilityStartTimeSec - 1e-9 && t <= layer.visibilityEndTimeSec + 1e-9
})

/**
 * 新增动画片段按钮是否可用：图层未锁定且播放头位于可见区间内。
 */
const canAddAnimationClip = computed(
  () => !activeLayerLocked.value && playheadInsideActiveLayerVisibility.value,
)

const addAnimationClipDisabledTitle = computed(() => {
  if (activeLayerLocked.value) return store.tUi('timeline.addAnimationLocked')
  if (!playheadInsideActiveLayerVisibility.value) return store.tUi('timeline.addAnimationOutOfVisibility')
  return undefined
})

/**
 * 时间轴缩放滑块变更：同步到 store。
 *
 * @param e - 原生 input 事件
 */
function onGranularityInput(e: { currentTarget: EventTarget | null }): void {
  const el = e.currentTarget
  if (!(el instanceof HTMLInputElement)) return
  store.setTimelineGranularity(Number(el.value))
}

/**
 * @param ev - 行内操作按钮事件
 * @param layerId - 图层 id
 */
function onToggleLayerLock(ev: MouseEvent, layerId: string): void {
  ev.stopPropagation()
  store.toggleLayerLocked(layerId)
}

/**
 * @param ev - 行内操作按钮事件
 * @param layerId - 图层 id
 */
function onDuplicateLayer(ev: MouseEvent, layerId: string): void {
  ev.stopPropagation()
  store.duplicateLayer(layerId)
}
</script>

<template>
  <section class="panel" :aria-label="store.tUi('timeline.panelLabel')">
    <div class="toolbar">
      <div class="left toolbarLeftCluster">
        <button
          type="button"
          class="btn btnAddClip"
          :disabled="!canAddAnimationClip"
          :title="addAnimationClipDisabledTitle"
          @click="store.addAnimationClipAtPlayhead"
        >
          <svg class="btnAddClipIcon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6" />
            <path
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
              d="M12 8.25v7.5M8.25 12h7.5"
            />
          </svg>
          <span class="btnAddClipLabel">{{ store.tUi('timeline.addAnimation') }}</span>
        </button>
        <span class="toolbarDivider" aria-hidden="true" />
        <div class="granularityTimeline" :aria-label="store.tUi('timeline.scaleControl')">
          <button
            type="button"
            class="mini"
            :aria-label="store.tUi('timeline.zoomOut')"
            @click="store.bumpTimelineGranularity(-5)"
          >
            −
          </button>
          <input
            class="zrange"
            type="range"
            min="0"
            max="100"
            step="1"
            :value="timelineGranularity"
            :aria-label="store.tUi('timeline.zoomSlider')"
            @input="onGranularityInput"
          />
          <button
            type="button"
            class="mini"
            :aria-label="store.tUi('timeline.zoomIn')"
            @click="store.bumpTimelineGranularity(5)"
          >
            +
          </button>
        </div>
        <span class="toolbarDivider" aria-hidden="true" />
        <div class="historyCluster">
          <button
            type="button"
            class="histBtn"
            :aria-label="store.tUi('nav.undo')"
            :title="`${store.tUi('nav.undo')} (Ctrl+Z)`"
            :disabled="!canUndoEditor"
            @click="store.undoEditor()"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                fill="currentColor"
                d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L3 9v6h6l-2.62-2.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"
              />
            </svg>
          </button>
          <button
            type="button"
            class="histBtn"
            :aria-label="store.tUi('nav.redo')"
            :title="`${store.tUi('nav.redo')} (Ctrl+Y / Ctrl+Shift+Z)`"
            :disabled="!canRedoEditor"
            @click="store.redoEditor()"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                fill="currentColor"
                d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.95 0 3.73.72 5.12 1.88L13 15h6V9l-2.6 2.6z"
              />
            </svg>
          </button>
        </div>
      </div>

      <button
        type="button"
        class="play"
        :aria-pressed="isPlaying ? 'true' : 'false'"
        :aria-label="isPlaying ? store.tUi('timeline.pause') : store.tUi('timeline.play')"
        @click="store.togglePlayback"
      >
        <svg v-if="!isPlaying" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path fill="currentColor" d="M8 5v14l11-7z" />
        </svg>
        <svg v-else viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path fill="currentColor" d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />
        </svg>
      </button>

      <div class="right">
        <div class="readout">
          <span class="muted">{{ `${store.tUi('timeline.current')}:` }}</span>
          <span class="mono">{{ formattedTime }}</span>
          <span class="muted">{{ store.tUi('timeline.seconds') }}</span>
        </div>
        <label class="tlabel" for="total-time">{{ store.tUi('timeline.time') }}</label>
        <input
          id="total-time"
          class="tinp"
          type="number"
          min="1"
          max="600"
          step="1"
          :value="totalDurationSeconds"
          :aria-label="store.tUi('timeline.totalTimelineSeconds')"
          @change="
            store.setTotalDurationSeconds(Number(($event.target as HTMLInputElement).value || 1))
          "
        />
        <span class="muted">{{ store.tUi('timeline.seconds') }}</span>
        <label class="tlabel" for="snap-step">{{ store.tUi('timeline.snap') }}</label>
        <input
          id="snap-step"
          class="tinp snapInp"
          type="number"
          min="0"
          max="10"
          step="0.05"
          :value="timelineSnapStepSec"
          :aria-label="store.tUi('timeline.snapHint')"
          :title="store.tUi('timeline.snapHint')"
          @change="
            store.setTimelineSnapStepSec(Number(($event.target as HTMLInputElement).value || 0))
          "
        />
      </div>
    </div>

    <div class="bodyStack">
      <div class="timelineBody">
        <div class="headLeft">
          <div class="lhead">{{ store.tUi('timeline.list') }}</div>
        </div>
        <div class="headRight">
          <div class="thead">{{ store.tUi('timeline.tracks') }}</div>
        </div>
        <div ref="bodyScrollRef" class="bodyScroll">
          <div
            class="listStack"
            :aria-label="store.tUi('timeline.layersAria')"
            @wheel="forwardVerticalWheelToSharedScroll"
          >
            <div class="layerSpacer" aria-hidden="true" />
            <p v-if="animationLayers.length === 0" class="empty">{{ store.tUi('timeline.empty') }}</p>
            <ul v-else class="layerList" :aria-label="store.tUi('timeline.layerListAria')">
              <li
                v-for="layer in animationLayers"
                :key="layer.id"
                class="layerRow"
                :class="{ selected: layer.id === selectedLayerId }"
                :aria-current="layer.id === selectedLayerId ? 'true' : undefined"
              >
                <button
                  type="button"
                  class="layerMain"
                  :aria-label="
                    layer.id === selectedLayerId
                      ? store.tUi('timeline.layerSelected', { name: layer.name })
                      : store.tUi('timeline.layerSelect', { name: layer.name })
                  "
                  @click="store.selectAnimationLayer(layer.id)"
                >
                  <span class="layerName">{{ layer.name }}</span>
                </button>
                <div class="layerActions">
                  <button
                    type="button"
                    class="layerActionBtn"
                    :class="{ active: layer.locked }"
                    :aria-label="layer.locked ? store.tUi('timeline.unlockLayer') : store.tUi('timeline.lockLayer')"
                    :title="layer.locked ? store.tUi('timeline.unlockLayer') : store.tUi('timeline.lockLayer')"
                    @click="onToggleLayerLock($event, layer.id)"
                  >
                    <svg v-if="layer.locked" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M12 2a4 4 0 0 0-4 4v3H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V6a4 4 0 0 0-4-4Zm-2 7V6a2 2 0 1 1 4 0v3h-4Z"
                      />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M17 9h-6V6a4 4 0 1 1 8 0h-2a2 2 0 1 0-4 0v3H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Z"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="layerActionBtn"
                    :aria-label="store.tUi('timeline.duplicateLayer')"
                    :title="store.tUi('timeline.duplicateLayer')"
                    @click="onDuplicateLayer($event, layer.id)"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M16 1H6a2 2 0 0 0-2 2v12h2V3h10V1Zm3 4H10a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H10V7h9v14Z"
                      />
                    </svg>
                  </button>
                </div>
              </li>
            </ul>
          </div>
          <ImockupTimeline class="timelinePane" @wheel="forwardVerticalWheelToSharedScroll" />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.panel {
  border-top: 1px solid var(--im-border);
  background: var(--im-surface);
  box-sizing: border-box;
  height: var(--panel-bottom, auto);
  min-height: var(--im-timeline-h);
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  grid-template-areas: 'left play right';
  gap: 0.75rem;
  align-items: center;
  padding: 0.55rem 0.65rem;
  border-bottom: 1px solid var(--im-border);
}

.left,
.right {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.left {
  grid-area: left;
  flex: 1 1 auto;
  justify-self: start;
  overflow: hidden;
}

.toolbarLeftCluster {
  flex-wrap: nowrap;
  row-gap: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  padding-bottom: 0.1rem;
  max-width: 100%;
}

.toolbarDivider {
  flex: 0 0 auto;
  width: 1px;
  align-self: stretch;
  min-height: 1.75rem;
  background: var(--im-border);
  margin: 0 0.05rem;
}

.btnAddClip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  position: relative;
}

.btnAddClipIcon {
  flex: 0 0 auto;
  color: var(--im-text);
}

.historyCluster {
  display: inline-flex;
  align-items: center;
  gap: 0.08rem;
}

.histBtn {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  border: 0;
  background: transparent;
  color: var(--im-text);
  display: grid;
  place-items: center;
  cursor: pointer;
  padding: 0;
}

.histBtn:hover:not(:disabled) {
  background: var(--im-accent-ghost);
}

.histBtn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.histBtn:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.right {
  grid-area: right;
  flex: 0 1 auto;
  justify-self: end;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-left: 0;
}

.btn {
  border-radius: 10px;
  border: 1px solid var(--im-border);
  background: var(--im-surface);
  color: var(--im-text);
  font-weight: 750;
  font-size: 0.8125rem;
  padding: 0.45rem 0.65rem;
  cursor: pointer;
  white-space: nowrap;
}

.btn:hover {
  background: var(--im-elevated);
}

.btn:disabled {
  border-color: color-mix(in srgb, var(--im-border) 85%, transparent);
  background: color-mix(in srgb, var(--im-surface) 92%, var(--im-border-2));
  color: var(--im-muted);
  opacity: 0.58;
  cursor: not-allowed;
}

.btn:disabled .btnAddClipIcon {
  color: var(--im-muted);
}

.btn:disabled:hover {
  background: color-mix(in srgb, var(--im-surface) 92%, var(--im-border-2));
}

.btn:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.granularityTimeline {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.15rem 0.4rem;
  border: 1px solid var(--im-border);
  border-radius: 999px;
  background: var(--im-surface);
  flex-shrink: 0;
}

.mini {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-weight: 900;
}

.mini:hover {
  background: var(--im-accent-ghost);
}

.zrange {
  width: clamp(100px, 18vw, 200px);
  accent-color: var(--im-accent);
  min-width: 84px;
}

@media (max-width: 1280px) {
  .btnAddClip {
    flex-shrink: 0;
  }

  .btnAddClipLabel {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .granularityTimeline {
    flex-shrink: 1;
    min-width: 0;
  }

  .zrange {
    width: clamp(64px, 20vw, 140px);
    min-width: 56px;
  }

  .toolbar {
    gap: 0.5rem;
    padding: 0.5rem 0.5rem;
  }
}

.play {
  width: 52px;
  height: 52px;
  border-radius: 999px;
  border: 1px solid var(--im-interactive-bg);
  background: var(--im-interactive-bg);
  color: var(--im-interactive-fg);
  display: grid;
  place-items: center;
  cursor: pointer;
  grid-area: play;
  justify-self: center;
  flex: 0 0 auto;
}

.play:hover {
  filter: brightness(1.05);
}

.play:focus-visible {
  outline: 3px solid var(--im-focus-ring);
  outline-offset: 2px;
}

.readout {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: 0.8125rem;
}

.muted {
  color: var(--im-muted);
  font-weight: 650;
}

.mono {
  font-variant-numeric: tabular-nums;
  font-weight: 900;
  color: var(--im-text);
}

.tlabel {
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--im-muted-2);
}

.snapInp {
  width: 3.75rem;
}

.tinp {
  width: 4.5rem;
  border-radius: 10px;
  border: 1px solid var(--im-border);
  padding: 0.35rem 0.45rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  outline: none;
}

.tinp:focus-visible {
  border-color: var(--im-accent);
  box-shadow: 0 0 0 3px var(--im-focus-ring);
}

.bodyStack {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.timelineBody {
  --im-timeline-lane-h: 2.35rem;
  /** 与右侧轨道 `trackWide` 顶部内边距一致，保证标尺行与左侧占位对齐 */
  --im-timeline-ruler-gutter-y: 0.5rem;
  /** 横向留白在滚动宿主上，时间刻度与 `trackWide` 同宽；播放头在 0%/100% 时圆钮落在留白内 */
  --im-timeline-track-gutter-x: max(12px, 0.75rem);
  /** 图层列表与轨道各行之间的竖直小间距 */
  --im-timeline-lane-gap: 0.22rem;

  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(240px, 380px) minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  align-items: stretch;
  gap: 0;
}

.bodyScroll {
  grid-column: 1 / 3;
  grid-row: 2;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(240px, 380px) minmax(0, 1fr);
  grid-auto-rows: minmax(0, 1fr);
  align-items: stretch;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  scrollbar-width: thin;
}

.headLeft {
  grid-column: 1;
  grid-row: 1;
  border-right: 1px solid var(--im-border);
  padding: 0.55rem 0.65rem 0.4rem;
  box-sizing: border-box;
}

.headRight {
  grid-column: 2;
  grid-row: 1;
  padding: 0.55rem 0.65rem 0.4rem;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
}

.lhead,
.thead {
  font-size: 0.6875rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--im-muted-2);
}

.listStack {
  align-self: stretch;
  border-right: 1px solid var(--im-border);
  padding: var(--im-timeline-ruler-gutter-y) 0.65rem 0.55rem;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.layerSpacer {
  position: sticky;
  top: 0;
  z-index: 2;
  flex: 0 0 auto;
  height: var(--im-timeline-lane-h);
  box-sizing: border-box;
  border-bottom: 1px solid transparent;
  background: var(--im-surface);
}

.timelinePane {
  align-self: stretch;
  min-width: 0;
  min-height: 0;
  height: auto;
  display: flex;
  flex-direction: column;
  padding: var(--im-timeline-ruler-gutter-y) 0.65rem 0.55rem 0.65rem;
  box-sizing: border-box;
  overflow: visible;
}

.empty {
  margin: 0;
  border: 1px dashed var(--im-border-strong);
  border-radius: 10px;
  padding: 0.65rem 0.5rem;
  text-align: center;
  color: var(--im-muted);
  font-size: 0.8125rem;
}

.layerList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--im-timeline-lane-gap);
}

.layerRow {
  display: flex;
  align-items: center;
  height: var(--im-timeline-lane-h);
  min-height: var(--im-timeline-lane-h);
  max-height: var(--im-timeline-lane-h);
  box-sizing: border-box;
  border-radius: 8px;
  border: 0;
  border-bottom: 1px solid var(--im-border);
  transition: background 0.12s ease, box-shadow 0.12s ease, border-bottom-color 0.12s ease;
  background: var(--im-surface);
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 750;
  color: var(--im-text);
  user-select: none;
}

.layerRow:hover {
  background: var(--im-elevated);
}

.layerRow:focus-within {
  outline: 2px solid var(--im-accent);
  outline-offset: 0;
}

.layerRow.selected {
  background: var(--im-selection-bg);
  border-bottom-color: var(--im-selection-border);
  box-shadow: inset 0 0 0 1px var(--im-selection-outline);
}

.layerMain {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 0;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 0 0.6rem;
  border-radius: 7px;
  cursor: pointer;
  font: inherit;
  font-weight: 750;
  color: inherit;
  box-sizing: border-box;
}

.layerActions {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding-right: 0.4rem;
}

.layerActionBtn {
  width: 1.65rem;
  height: 1.65rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--im-muted);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.layerActionBtn:hover {
  background: var(--im-interactive-soft);
  color: var(--im-text);
}

.layerActionBtn.active {
  color: var(--im-accent);
  background: var(--im-accent-ghost);
}

.layerMain:hover {
  background: var(--im-interactive-soft);
}

.layerMain:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 0;
}

.layerName {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 900px) {
  .panel {
    height: auto;
  }

  .toolbar {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-template-areas:
      'left right'
      'play play';
  }

  .timelineBody {
    grid-template-columns: minmax(150px, 210px) minmax(0, 1fr);
  }
}

@media (max-width: 720px) {
  .toolbar {
    grid-template-columns: 1fr;
    grid-template-areas:
      'left'
      'play'
      'right';
    align-items: center;
  }

  .left,
  .right {
    width: 100%;
    flex: 1 1 auto;
    justify-self: stretch;
    justify-content: flex-start;
  }

  .right {
    justify-content: flex-end;
  }

  .play {
    justify-self: center;
  }

  .timelineBody {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .bodyScroll {
    display: block;
    min-height: 0;
    overflow-y: visible;
  }

  .headLeft {
    border-right: 0;
    border-bottom: 1px solid var(--im-border);
  }

  .headRight {
    border-bottom: 1px solid var(--im-border);
  }

  .listStack {
    border-right: 0;
    border-bottom: 1px solid var(--im-border);
    max-height: min(40vh, 280px);
    overflow-y: auto;
  }

  .timelinePane {
    flex: 1;
    min-height: min(36vh, 320px);
  }
}
</style>
